import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  type DataTableToolbarProps,
  type FacetedFilterOption,
  FacetedFilterStandalone,
  type GetOptionsByValueBase,
  type OptionsByValue,
  PrimaryFullScreenSpinner,
  toast,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { compareDesc } from "date-fns";
import { X } from "lucide-react";
import { Suspense, useMemo, useState } from "react";
import { RiAddLine } from "react-icons/ri";
import { columns } from "~/components/FXTrades/columns";
import { NewFXTradeModal } from "~/components/FXTrades/NewFXTradeModal";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  fXTradeStatusValues,
  fXTradeTypeValues,
} from "~/gql/client/enumValues";
import type { FxTradeList_FxTradesFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/fx-trades")({
  head: () => opsHead("FX Trades"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { data, error } = useSuspenseQuery(
    graphql(`
      query FXTradesIndexPage {
        ...FXTradeList_FXTrades
        ...NewFXTradeModal_Organisations
      }

      fragment FXTradeList_FXTrades on Query {
        fxTrades {
          id
          amount {
            amount
            currency
          }
          sellAmount {
            amount
            currency
          }
          buyAmount {
            amount
            currency
          }
          externalId
          operation
          organisation {
            id
            name
          }
          processAfterDateTime
          status
          valueDate
          tradeType
        }
      }
   `),
  );
  if (error) {
    throw error;
  }
  if (!data || data.fxTrades === null || data.fxTrades === undefined) {
    throw new NotFoundError();
  }

  const navigate = useNavigate();
  const [newModalOpen, setNewModalOpen] = useState<boolean>(false);
  const [triggerCron, { loading: isSyncing }] = useMutation(
    graphql(`
      mutation AdminFXTradesTriggerCron($input: TriggerCronInput!) {
        triggerCron(input: $input) {
          jobId
          taskId
        }
      }
    `),
  );

  const trades = data.fxTrades
    .slice()
    .sort((a, b) =>
      compareDesc(a.processAfterDateTime, b.processAfterDateTime),
    );

  return (
    <AdminPageContainer testId="admin-fx-trades">
      <AdminPageHeader
        title="FX Trades"
        buttons={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={isSyncing}
              onClick={() => {
                void triggerCron({
                  variables: {
                    input: {
                      taskId: "pollEburyClientsCronTask",
                    },
                  },
                })
                  .then(() => {
                    toast({
                      title: "Ebury sync triggered",
                      kind: "success",
                      autoHide: true,
                    });
                  })
                  .catch((error) => {
                    toast({
                      kind: "error",
                      title: "Failed to trigger Ebury sync",
                      description: String(error),
                    });
                  });
              }}
            >
              Sync with Ebury
            </Button>
            <Button size="sm" onClick={() => setNewModalOpen(true)}>
              <RiAddLine className="mr-1" />
              New FX trade
            </Button>
          </div>
        }
      />
      <DataTable
        data={trades}
        columns={columns}
        onRowClick={(row) => {
          void navigate({
            to: "/fx-trades/$fxTradeId",
            params: { fxTradeId: row.original.id },
          });
        }}
        className="overflow-hidden border border-zinc-200"
        toolbarComponent={DataTableToolbar}
        initialColumnVisibility={{ tradeType: false }}
      />
      <NewFXTradeModal
        open={newModalOpen}
        onOpenChange={setNewModalOpen}
        organisationOptions={data.organisations}
      />
    </AdminPageContainer>
  );
}

type FXTradeRow = NonNullable<FxTradeList_FxTradesFragment["fxTrades"]>[number];

function DataTableToolbar({ table }: DataTableToolbarProps<FXTradeRow>) {
  const isFiltered =
    table.getPreFilteredRowModel().rows.length >
    table.getFilteredRowModel().rows.length;

  const preFilteredRows = table.getPreFilteredRowModel().rows;

  const organisationIdOptionsByValue = useMemo<OptionsByValue>(
    () => getOrganisationIdOptionsByValue({ preFilteredRows }),
    [preFilteredRows],
  );

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 flex-wrap items-center gap-1">
        {table.getColumn("status") && (
          <FacetedFilterStandalone
            column={table.getColumn("status")}
            title="Status"
            options={fXTradeStatusValues.map((value) => ({
              label: prettyPrintEnum(value),
              value,
            }))}
          />
        )}
        {table.getColumn("tradeType") && (
          <FacetedFilterStandalone
            column={table.getColumn("tradeType")}
            title="Trade type"
            options={fXTradeTypeValues.map((value) => ({
              label: prettyPrintEnum(value),
              value,
            }))}
          />
        )}
        {table.getColumn("organisationId") && (
          <FacetedFilterStandalone
            column={table.getColumn("organisationId")}
            title="Organisation"
            options={Object.values(organisationIdOptionsByValue)}
          />
        )}
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <X className="ml-2 size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

type GetOptionsByValue = GetOptionsByValueBase<FXTradeRow>;

const getOrganisationIdOptionsByValue: GetOptionsByValue = ({
  preFilteredRows,
}) =>
  preFilteredRows.reduce(
    (acc, row) => {
      const organisation = row.original.organisation;

      if (organisation && !acc[organisation.id]) {
        acc[organisation.id] = {
          label: organisation.name,
          value: organisation.id,
        };
      }
      return acc;
    },
    {} as Record<string, FacetedFilterOption>,
  );
